/** Re-export of shared AI policy mapping result logic (convex/model). */
export * from "../../convex/model/policyMapping";

import type {
  PolicyMappingComplianceStatus,
  PolicyMappingConfidence,
  PolicyMappingVerificationStatus,
  PolicyObligationCoverageStatus,
} from "../../convex/model/policyMapping";

const COVERAGE_LABELS = {
  covered: "Covered",
  partial: "Partial",
  missing: "Missing",
  unclear: "Unclear",
  needsLegalReview: "Needs legal review",
} satisfies Record<PolicyObligationCoverageStatus, string>;

const COVERAGE_TONES = {
  covered: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  partial: "bg-amber-50 text-amber-700 ring-amber-200",
  missing: "bg-red-50 text-red-700 ring-red-200",
  unclear: "bg-slate-100 text-slate-600 ring-slate-200",
  needsLegalReview: "bg-purple-50 text-purple-700 ring-purple-200",
} satisfies Record<PolicyObligationCoverageStatus, string>;


const COMPLIANCE_LABELS = {
  complies: "Mapped · complies",
  doesNotComply: "Mapped · does not comply",
  partiallyComplies: "Mapped · partially complies",
  unclear: "Mapped · unclear",
  needsLegalReview: "Needs legal review",
  notMapped: "Not mapped",
} satisfies Record<PolicyMappingComplianceStatus, string>;

const COMPLIANCE_TONES = {
  complies: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  doesNotComply: "bg-red-50 text-red-700 ring-red-200",
  partiallyComplies: "bg-amber-50 text-amber-700 ring-amber-200",
  unclear: "bg-slate-100 text-slate-600 ring-slate-200",
  needsLegalReview: "bg-purple-50 text-purple-700 ring-purple-200",
  notMapped: "bg-slate-100 text-slate-500 ring-slate-200",
} satisfies Record<PolicyMappingComplianceStatus, string>;

const CONFIDENCE_LABELS = {
  high: "High confidence",
  medium: "Medium confidence",
  low: "Low confidence",
} satisfies Record<PolicyMappingConfidence, string>;

const CONFIDENCE_TONES = {
  high: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  medium: "bg-amber-50 text-amber-700 ring-amber-200",
  low: "bg-slate-100 text-slate-600 ring-slate-200",
} satisfies Record<PolicyMappingConfidence, string>;

const VERIFICATION_LABELS = {
  aiUnverified: "AI unverified",
  humanVerified: "Human verified",
  rejected: "Rejected",
} satisfies Record<PolicyMappingVerificationStatus, string>;

const VERIFICATION_TONES = {
  aiUnverified: "bg-amber-50 text-amber-700 ring-amber-200",
  humanVerified: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  rejected: "bg-slate-100 text-slate-600 ring-slate-200",
} satisfies Record<PolicyMappingVerificationStatus, string>;

export const policyMappingCoverageLabel = (status: PolicyObligationCoverageStatus): string =>
  COVERAGE_LABELS[status];

export const policyMappingCoverageToneClass = (status: PolicyObligationCoverageStatus): string =>
  COVERAGE_TONES[status];

export const policyMappingConfidenceLabel = (confidence: PolicyMappingConfidence): string =>
  CONFIDENCE_LABELS[confidence];

export const policyMappingConfidenceToneClass = (confidence: PolicyMappingConfidence): string =>
  CONFIDENCE_TONES[confidence];

export const policyMappingVerificationLabel = (status: PolicyMappingVerificationStatus): string =>
  VERIFICATION_LABELS[status];

export const policyMappingVerificationToneClass = (status: PolicyMappingVerificationStatus): string =>
  VERIFICATION_TONES[status];

export const policyMappingComplianceLabel = (status: PolicyMappingComplianceStatus): string =>
  COMPLIANCE_LABELS[status];

export const policyMappingComplianceToneClass = (status: PolicyMappingComplianceStatus): string =>
  COMPLIANCE_TONES[status];

export const policyMappingMappedLabel = (policySectionCount: number): string =>
  policySectionCount > 0 ? "Mapped" : "Not mapped";

export const policyMappingMappedToneClass = (policySectionCount: number): string =>
  policySectionCount > 0
    ? "bg-sky-50 text-sky-700 ring-sky-200"
    : "bg-slate-100 text-slate-500 ring-slate-200";
